'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { toSlug } from '@framework/url/helpers/toSlug';
import AssetClass from '@/creditflowresearch/domain/AssetClass';
import Router, { useRouter, getQueryString } from '@framework/router';
import { IAssetClass } from '@/creditflowresearch/domain/vo/IAssetClass';

export const useTraceAssetClasses = (initial?: IAssetClass[]) => {
  const [assetsClasses, setAssetsClasses] = useState<IAssetClass[]>(initial || []);
  const { query } = useRouter();
  const { assetClass: queryAssetClass, ...rest } = query;
  const [
    currentAssetClass,
    setCurrentAssetClass,
  ] = useState<IAssetClass | null>(AssetClass.getByAbbreviation(assetsClasses, queryAssetClass as string));
  const search = getQueryString(rest);
  useEffect(() => setAssetsClasses(initial), []);

  const handleAssetClassClick = (assetClass) => {
    const sluggedAssetClassAbbr = toSlug(assetClass.abbreviation);
    if (sluggedAssetClassAbbr !== assetClass) {
      setCurrentAssetClass(assetClass);
      Router.replace(`/trace-${ toSlug(assetClass.abbreviation) }?${ search }`, undefined, { shallow: true });
    }
  };

  return {
    assetsClasses,
    handleAssetClassClick,
    currentAssetClass,
    setCurrentAssetClass,
  };
};

type TraceAssetClassesContextType = ReturnType<typeof useTraceAssetClasses>;

export const AssetClassesContext = createContext<TraceAssetClassesContextType>(null);

export const useTraceAssetClassesContext = () => {
  const context = useContext(AssetClassesContext);
  if (!context) {
    throw new Error('useTraceAssetClassesContext must be used within the AssetClassesContext');
  }
  return context;
};

interface IProps {
  initial?: IAssetClass[],
  children: React.ReactNode,
}

const TraceAssetClassesContextProvider = ({ initial, children }: IProps) => (
  <AssetClassesContext.Provider value={ useTraceAssetClasses(initial) }>
    { children }
  </AssetClassesContext.Provider>
);

export default TraceAssetClassesContextProvider;
